// 서식 에디터 (Quill) — 업데이트 내역 작성용.
// 이미지는 버튼·붙여넣기·끌어놓기 모두 받아서 긴 변 1280px 이하로 줄여 본문에 넣는다(별도 업로드 서버 없이 DB에 저장).
import React, { useMemo, useRef, useState } from 'react';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const MAX_SIDE = 1280;

function shrinkImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const scale = Math.min(1, MAX_SIDE / Math.max(img.width, img.height));
        if (scale === 1 && file.size < 300 * 1024) { resolve(reader.result); return; }
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL(file.type === 'image/png' ? 'image/png' : 'image/jpeg', 0.85));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

const RichEditor = ({ value, onChange, placeholder, height = 360 }) => {
  const ref = useRef(null);
  // 에디터에는 Quill 이 만든 HTML 그대로(inner)를 돌려주고, 바깥에는 정리한 HTML 을 넘긴다.
  // (정리한 값을 그대로 value 로 넣으면 에디터 내용과 달라 매번 다시 그리며 멈춘다)
  const [inner, setInner] = useState(value || '');

  const insertImages = async (quill, range, files) => {
    const imgs = [...files].filter((f) => /^image\/(png|jpe?g|gif|webp)$/.test(f.type));
    let at = range ? range.index : quill.getLength();
    for (const f of imgs) {
      // eslint-disable-next-line no-await-in-loop
      const url = await shrinkImage(f);
      quill.insertEmbed(at, 'image', url, Quill.sources.USER);
      at += 1;
    }
    quill.setSelection(at, 0, Quill.sources.SILENT);
  };

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ align: [] }],
        ['blockquote', 'link', 'image'],
        ['clean'],
      ],
      handlers: {
        image() {
          const quill = this.quill;
          const input = document.createElement('input');
          input.type = 'file';
          input.accept = 'image/png,image/jpeg,image/gif,image/webp';
          input.multiple = true;
          input.onchange = () => insertImages(quill, quill.getSelection(true), input.files);
          input.click();
        },
      },
    },
    uploader: {
      mimetypes: ['image/png', 'image/jpeg', 'image/gif', 'image/webp'],
      handler(range, files) { insertImages(this.quill, range, files); },
    },
  }), []);

  return (
    <div className="rich-editor" style={{ '--rich-editor-height': `${height}px` }}>
      {/* Quill 2 의 semantic HTML 은 공백을 전부 &nbsp; 로 바꿔 긴 줄이 줄바꿈되지 않는다 → 바깥으로는 일반 공백으로 */}
      <ReactQuill
        ref={ref} theme="snow" value={inner} modules={modules} placeholder={placeholder}
        onChange={(html) => { setInner(html); onChange(html.replace(/&nbsp;/g, ' ')); }}
      />
    </div>
  );
};

export default RichEditor;
